import type { DemoScenarioResolved } from 'tio-data/demo/types';
import {
  isFeatureEnabled,
  type DemoSessionSelection,
} from 'tio-employee/utils/career-pathways-demo-data';
import {
  assertNever,
  type DemoFixtureIdentity,
  type DemoProductConfig,
  type DemoScenario,
} from 'tio-employee/demo/types';
import {
  buildDemoIdentityDoc,
  buildDemoIdentityRawDoc,
  buildDemoCompanyDoc,
  buildDemoEmployeeCatchAllDoc,
  buildDemoPersonDoc,
  resolveDemoFixtureIdentity,
} from 'tio-employee/demo/identity-fixtures';
import { buildAdminTableMatchers, buildReportDetailRawDoc } from 'tio-employee/demo/admin-fixtures';
import { buildPslfDemo } from 'tio-employee/demo/products/pslf';
import { buildTasDemo } from 'tio-employee/demo/products/tas';
import { buildContributionsDemo } from 'tio-employee/demo/products/contributions';
import { buildCareerPathwaysDemo } from 'tio-employee/demo/products/career-pathways';
import { buildWellnessDemo } from 'tio-employee/demo/products/wellness';

/**
 * Dispatch a product config to its builder. The exhaustive switch + assertNever
 * guarantees every product kind has a builder at compile time — adding a kind
 * to the union without a case is a type error.
 */
function runBuilder(
  config: DemoProductConfig,
  scenario: DemoScenario,
  identity: DemoFixtureIdentity
): DemoScenarioResolved {
  switch (config.kind) {
    case 'pslf':
      return buildPslfDemo(config, scenario, identity);
    case 'tas':
      return buildTasDemo(config, scenario, identity);
    case 'contributions':
      return buildContributionsDemo(config, scenario, identity);
    case 'career-pathways':
      return buildCareerPathwaysDemo(config, scenario, identity);
    case 'wellness':
      return buildWellnessDemo(config, scenario, identity);
    default:
      return assertNever(config);
  }
}

/**
 * Map the active demo selection onto the concrete product configs whose
 * fixtures should be served. Product availability is derived from the current
 * demo scenario's feature flags (e.g. a scenario with `pslf` enabled serves the
 * PSLF dashboard fixtures). Returns null when no demo products are active so
 * the handler stays a transparent pass-through.
 */
function scenarioFromSelection(selection: DemoSessionSelection): DemoScenario | null {
  const products: DemoProductConfig[] = [];

  if (isFeatureEnabled('pslf')) {
    products.push({ kind: 'pslf', scenarioId: 'full-journey' });
  }

  if (isFeatureEnabled('tuitionAssistance')) {
    products.push({
      kind: 'tas',
      scenarioId: 'in-progress',
      taPaymentModelId: selection.taPaymentModelId ?? 'reimbursement',
    });
  }

  if (isFeatureEnabled('studentLoanRepayment')) {
    products.push({ kind: 'contributions', scenarioId: 'in-progress' });
  }

  if (products.length === 0) {
    return null;
  }

  return {
    persona: selection.userScenarioId ?? 'default',
    client: selection.clientId,
    role: selection.roleId,
    products,
  };
}

/**
 * The session-identity matchers, served for EVERY active demo session
 * independent of which products are enabled (a product-less selection — e.g.
 * wellness/career-pathways — must still get a demo `/me`). Serves the fabricated
 * `/me` document on both seams: the raw `/me` fetch (interceptor) and the
 * `findRecord('user', …)` the authenticated route re-issues (request manager).
 */
function identityResolved(selection: Required<DemoSessionSelection>): DemoScenarioResolved {
  return {
    // findRecord('user') → store.push (already-normalized, camelCase keys).
    matchers: [
      {
        matcher: { kind: 'op', op: 'findRecord', type: 'user' },
        docs: [buildDemoIdentityDoc(selection)],
      },
    ],
    seed: [],
    // /me raw fetch → session-context pushPayload → serializer (raw-API, dasherized keys).
    fetchMatchers: [
      { urlIncludes: '/me?include=', method: 'GET', json: buildDemoIdentityRawDoc(selection) },
    ],
  };
}

// A complete, wire-faithful EMPTY OpenSearch response (matches the
// `OpenSearchResponse` shape in tio-data/models/search: hits.total {value,
// relation}, hits.maxScore, took, timedOut) so every consumer — including any
// that reads those fields without defaulting — gets a well-formed zero-result
// payload. A safe inert result for any demo search not otherwise scripted.
const EMPTY_SEARCH_RESPONSE = {
  took: 0,
  timedOut: false,
  hits: { total: { value: 0, relation: 'eq' }, maxScore: 0, hits: [] },
  aggregations: {},
};

// Match ONLY the OpenSearch search endpoint (`…/api/v2/search`, optionally with a
// query string) — never `…/api/v2/search.csv` (downloadCsv), `…/api/v2/search/…`
// subroutes (mapping/fields/explain/stats), or `…/api/v2/searches`.
const matchesSearchEndpoint = (url: string) => /\/api\/v2\/search(?:\?|$)/.test(url);

/**
 * Compile the active demo selection into the matchers + seed the tio-data demo
 * handler consumes. ALWAYS returns the identity matchers (so the whole session
 * is demo-coherent), plus any active product's matchers.
 */
export function resolveDemoScenario(
  selection: Required<DemoSessionSelection>
): DemoScenarioResolved {
  const identity = identityResolved(selection);
  const scenario = scenarioFromSelection(selection);

  const matchers: DemoScenarioResolved['matchers'] = [...identity.matchers];
  const seed: NonNullable<DemoScenarioResolved['seed']> = [...(identity.seed ?? [])];
  const fetchMatchers: NonNullable<DemoScenarioResolved['fetchMatchers']> = [
    ...(identity.fetchMatchers ?? []),
  ];

  if (scenario) {
    const fixtureIdentity = resolveDemoFixtureIdentity(selection);
    for (const product of scenario.products) {
      const part = runBuilder(product, scenario, fixtureIdentity);
      matchers.push(...part.matchers);
      if (part.seed) {
        seed.push(...part.seed);
      }
      if (part.fetchMatchers) {
        fetchMatchers.push(...part.fetchMatchers);
      }
    }
  }

  // Defense-in-depth: a catch-all `company` findRecord matcher, appended LAST so
  // any product-specific company fixture (e.g. PSLF's) still wins via the
  // handler's first-match-wins lookup. The handler echoes the requested id, so a
  // company lookup that NO product scripted — including ids reachable only via a
  // sideloaded relationship like `demo-slr-company-1` — resolves to the demo
  // company instead of falling through to the real API and 404-ing. Without
  // this, an active demo on contributions/TAS (which sideload but never register
  // a company findRecord) breaks with "demo-slr-company-1 could not be found".
  matchers.push({
    matcher: { kind: 'op', op: 'findRecord', type: 'company' },
    docs: [buildDemoCompanyDoc(selection)],
  });

  // Defense-in-depth: a catch-all OpenSearch matcher, appended LAST so any
  // product-specific search (e.g. an approval-queue TasProgramInstance fixture)
  // still wins via first-match-wins. The classic admin reporting + TAS approval
  // screens POST to /api/v2/search with the DEMO company id; with no matcher that
  // hits the real API, which 422s ("User does not belong to company id provided")
  // because the real session user is not a member of the demo company. Returning
  // an empty result keeps every demo search inert (a clean empty state) instead
  // of leaking to the live backend.
  matchers.push({
    matcher: { kind: 'builder', method: 'POST', urlMatches: matchesSearchEndpoint },
    content: EMPTY_SEARCH_RESPONSE,
  });

  // Catch-all `person` findRecord — same defense-in-depth as the company
  // catch-all above (echoed id; product person fixtures win first). Without it,
  // surfaces that look up the session person (e.g. the loans/account pages in a
  // product-less demo) fall through to the real API as /people/<demo-id> → 404.
  matchers.push({
    matcher: { kind: 'op', op: 'findRecord', type: 'person' },
    docs: [buildDemoPersonDoc(selection)],
  });

  // Catch-all `employee` findRecord — same pattern (echoed id; product
  // employee fixtures win first). Admin drill-ins reach arbitrary employee
  // ids from the directory/queue in products that registered no employee
  // findRecord of their own.
  matchers.push({
    matcher: { kind: 'op', op: 'findRecord', type: 'employee' },
    docs: [buildDemoEmployeeCatchAllDoc(selection)],
  });

  // The cross-product ADMIN tables (employee directory, eligibility files,
  // TAS payments, saved-reports listing) get authored demo collections —
  // these surfaces exist for the admin role in every product, so they're
  // registered here rather than in a product builder. After product matchers
  // (first-match-wins), before the empty catch-alls below.
  matchers.push(...buildAdminTableMatchers(resolveDemoFixtureIdentity(selection)));

  // Cross-product empty-collection catch-alls for everything else unscripted.
  // Appended LAST so product fixtures (e.g. the TAS template/instance queries,
  // the contributions transaction timeline) still win; unscripted reads
  // resolve to a clean empty state — never the live API (whose responses
  // would mix real data into a demo session).
  for (const type of [
    'tas-program-template',
    'accounting-transaction',
    // The admin employee drill-in queries PSLF forms for any employee row;
    // the PSLF product's own pslf-form fixture wins first when PSLF is on.
    'pslf-form',
    // Program-template admin screens load enum option lists (e.g.
    // AcademicPerformance) — empty keeps the dropdowns inert.
    'enum',
  ]) {
    // Pagination meta included: paginated admin tables read
    // `result.meta.page.total` without defaulting.
    matchers.push({
      matcher: { kind: 'op', op: 'query', type },
      docs: [{ data: [], meta: { page: { total: 0, offset: 0, limit: 50 } } }],
    });
  }

  // The SAME /api/v2/search seam is also hit by the `search` adapter's raw
  // `ajax` (tio-reporting analytics/charts via `adapterFor('search').search`),
  // which bypasses BOTH the request-manager handler AND window.fetch — so it
  // must be served via demoJsonFor (the search adapter consults it). Mirror the
  // empty result here so reporting search screens stay inert in demo, not 422.
  fetchMatchers.push({
    urlMatches: matchesSearchEndpoint,
    method: 'POST',
    json: EMPTY_SEARCH_RESPONSE,
  });

  // Cross-product raw-fetch catch-alls, appended LAST so any product-specific
  // matcher (e.g. the contributions accounting fixtures) still wins via
  // first-match-wins. These endpoints are reached from shared surfaces in
  // EVERY demo session, regardless of which products registered fixtures:
  //
  // - The employee service's `contributionsState` / `futurePayments` tracked
  //   functions call the EmployeeAdapter accounting endpoints (seam B,
  //   demoJsonFor) from product-agnostic components; the demo identity employee
  //   always carries an SLR plan, so they fire even in a TAS-only demo. An
  //   empty payments payload with NO `summary.total` makes consumers
  //   (fetchEmployerContributions) fall back to their local demo snapshot —
  //   empty-safe, never live.
  // - The custom telemetry service (tio-common/services/telemetry) flushes
  //   event batches and rrweb session-replay chunks to the API's public
  //   telemetry endpoints via plain `fetch`. A demo session must not emit its
  //   fabricated identity/interactions to the live API, so both are swallowed
  //   with an inert 200 while demo is active.
  fetchMatchers.push(
    { urlIncludes: '/accounting_payments', method: 'GET', json: { payments: [], summary: {} } },
    { urlIncludes: '/accounting_preview', method: 'GET', json: [] },
    { urlIncludes: '/api/public/telemetry', method: 'POST', json: {} },
    { urlIncludes: '/api/public/session-replay', method: 'POST', json: {} },
    // SLR alerts for the session person (loans/account surfaces fetch this in
    // every product). 'non-slr-never' is the wire value for "this person has
    // never had SLR" — a coherent empty state; the contributions product's
    // richer fixture wins first when SLR is in the demo.
    {
      urlIncludes: '/slr-alerts',
      method: 'GET',
      json: { page_state: 'non-slr-never', alerts: [], contribution_account: null },
    },
    // TAS limits status for cells where no product registered one ({ data: [] }
    // is the wire shape for "no limits configured").
    { urlIncludes: '/api/v2/limits/status', method: 'GET', json: { data: [] } },
    // Reporting metadata endpoints (report-viewer / tas-programs / chart
    // builder), reached via the search adapter's raw `ajax` (demoJsonFor).
    // `mapping: null` + `fields: { fields: [] }` push chart/report builders to
    // their static fallback field catalogs — fully client-side, never live.
    { urlIncludes: '/api/v2/search/mapping', method: 'GET', json: null },
    { urlIncludes: '/api/v2/search/fields', method: 'GET', json: { fields: [] } },
    { urlIncludes: '/api/v2/search/key-label-maps', method: 'GET', json: {} },
    { urlIncludes: '/api/v2/search/employee-custom-field-mappings', method: 'GET', json: [] },
    // Saved-report reads that reach the raw-fetch seam (non-SkipCache builder
    // GETs). Detail URLs need a SINGLE-resource doc (`data.attributes` reads);
    // the listing shape answers only the bare collection URL.
    {
      urlMatches: (url: string) => /\/api\/v2\/reports\/[^/?]+/.test(url),
      method: 'GET',
      json: buildReportDetailRawDoc(resolveDemoFixtureIdentity(selection)),
    },
    {
      urlMatches: (url: string) => /\/api\/v2\/reports(?:\?|$)/.test(url),
      method: 'GET',
      json: { data: [] },
    }
  );

  return { matchers, seed, fetchMatchers };
}
